import React, { useState, useRef } from 'react';
import './CreateRoute.css';

const CreateRoute = () => {
  const [selectedImage, setSelectedImage] = useState('');
  const [routeName, setRouteName] = useState('');
  const [difficulty, setDifficulty] = useState('6a');
  const [comment, setComment] = useState('');
  const [holds, setHolds] = useState([]);
  const [color, setColor] = useState('red');
  const [shape, setShape] = useState('circle');
  const [fill, setFill] = useState('outline');
  const [size, setSize] = useState('medium');
  const [holdType, setHoldType] = useState('normal');
  const [isSaving, setIsSaving] = useState(false);
  const imageRef = useRef(null);
  const wrapperRef = useRef(null);
  const lastTouchTimeRef = useRef(0);
  const [touchMoved, setTouchMoved] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState('1058 / 1158');

  const images = [
    'images/Overhang.jpg',
    // Add more images as needed
  ];

  // Helper to get displayed image area (with black bars)
  const getImageDisplayArea = () => {
    const img = imageRef.current;
    const wrapper = wrapperRef.current;
    if (!img || !wrapper || !img.naturalWidth || !img.naturalHeight ||
      !wrapper.offsetWidth || !wrapper.offsetHeight) {
      return null;
    }
    const naturalAR = img.naturalWidth / img.naturalHeight;
    const wrapperAR = wrapper.offsetWidth / wrapper.offsetHeight;
    let width, height, left, top;
    if (naturalAR > wrapperAR) {
      width = wrapper.offsetWidth;
      height = width / naturalAR;
      left = 0;
      top = (wrapper.offsetHeight - height) / 2;
    } else {
      height = wrapper.offsetHeight;
      width = height * naturalAR;
      top = 0;
      left = (wrapper.offsetWidth - width) / 2;
    }
    return { left, top, width, height };
  };

  // Mouse click handler
  const handleImageClick = (e) => {
    if (e.type === 'click' && lastTouchTimeRef.current && Date.now() - lastTouchTimeRef.current < 500) {
      return;
    }
    if (!selectedImage) return;
    if (holds.length >= 30) {
      alert("You cannot add more than 30 holds.");
      return;
    }
    const wrapper = wrapperRef.current;
    const displayArea = getImageDisplayArea();
    if (!displayArea) return;
    const { left, top, width, height } = displayArea;
    const wrapperRect = wrapper.getBoundingClientRect();
    const clickX = e.clientX - wrapperRect.left;
    const clickY = e.clientY - wrapperRect.top;
    const x = ((clickX - left) / width) * 100;
    const y = ((clickY - top) / height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return; // Only allow inside image
    setHolds((currentHolds) => [...currentHolds, { x, y, color, shape, fill, size, type: holdType }]);
  };

  // Touch handler
  const handleTouchStart = () => setTouchMoved(false);
  const handleTouchMove = () => setTouchMoved(true);
  const handleTouchEnd = (e) => {
    lastTouchTimeRef.current = Date.now();
    if (touchMoved) return;
    if (!selectedImage) return;
    if (holds.length >= 30) {
      alert("You cannot add more than 30 holds.");
      return;
    }
    const wrapper = wrapperRef.current;
    const displayArea = getImageDisplayArea();
    if (!displayArea || !e.changedTouches[0]) return;
    const { left, top, width, height } = displayArea;
    const wrapperRect = wrapper.getBoundingClientRect();
    const touch = e.changedTouches[0];
    const touchX = touch.clientX - wrapperRect.left;
    const touchY = touch.clientY - wrapperRect.top;
    const x = ((touchX - left) / width) * 100;
    const y = ((touchY - top) / height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;
    setHolds((currentHolds) => [...currentHolds, { x, y, color, shape, fill, size, type: holdType }]);
  };

  const handleSaveRoute = () => {
    const trimmedRouteName = routeName.trim();
    if (!selectedImage || !trimmedRouteName || !difficulty) {
      alert('Please fill out all fields.');
      return;
    }
    setIsSaving(true);
    const newRoute = {
      name: trimmedRouteName,
      difficulty,
      comment,
      image: selectedImage,
      holds,
    };
    fetch('https://bouldering-backend.vercel.app/api/routes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRoute),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Route save failed with status ${response.status}`);
        }
      })
      .then(() => {
        alert('Route saved successfully!');
        setRouteName('');
        setDifficulty('6a');
        setComment('');
        setSelectedImage('');
        setHolds([]);
      })
      .catch(error => {
        console.error('Error saving route', error);
        alert('Failed to save route.');
      })
      .finally(() => setIsSaving(false));
  };

  const handleUndo = () => setHolds((currentHolds) => currentHolds.slice(0, -1));

  return (
    <div className="create-route-container">
      <h1>Create Your Route</h1>
      <div className="section-title">Route Details</div>
      <label htmlFor="route-name">Route name:</label>
      <input
        id="route-name"
        type="text"
        placeholder="Route Name"
        value={routeName}
        onChange={(e) => setRouteName(e.target.value)}
      />
      <label htmlFor="route-comment">Comment:</label>
      <textarea
        id="route-comment"
        placeholder="Comment"
        className="comment-box"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />

      <div className="section-title">Hold Settings</div>
      <div className="dropdown-group">
        <div className="control-group">
          <label htmlFor="route-difficulty">Grade:</label>
          <select id="route-difficulty" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            {/* ...grade options... */}
            <option value="6a">6a</option>
            <option value="6a+">6a+</option>
            <option value="6b">6b</option>
            <option value="6b+">6b+</option>
            <option value="6c">6c</option>
            <option value="6c+">6c+</option>
            <option value="7a">7a</option>
            <option value="7a+">7a+</option>
            <option value="7b">7b</option>
            <option value="7b+">7b+</option>
            <option value="7c">7c</option>
            <option value="7c+">7c+</option>
            <option value="8a">8a</option>
            <option value="8a+">8a+</option>
            <option value="8b">8b</option>
            <option value="8b+">8b+</option>
            <option value="8c">8c</option>
            <option value="8c+">8c+</option>
            <option value="9a">9a</option>
            <option value="something fun">something fun</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="hold-color">Color:</label>
          <select id="hold-color" value={color} onChange={(e) => setColor(e.target.value)}>
            <option value="red">Red</option>
            <option value="green">Green</option>
            <option value="blue">Blue</option>
            <option value="yellow">Yellow</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="hold-shape">Shape:</label>
          <select id="hold-shape" value={shape} onChange={(e) => setShape(e.target.value)}>
            <option value="circle">Circle</option>
            <option value="square">Square</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="hold-fill">Fill:</label>
          <select id="hold-fill" value={fill} onChange={(e) => setFill(e.target.value)}>
            <option value="filled">Filled</option>
            <option value="outline">Outline</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="hold-size">Size:</label>
          <select id="hold-size" value={size} onChange={(e) => setSize(e.target.value)}>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="hold-type">Type:</label>
          <select id="hold-type" value={holdType} onChange={(e) => setHoldType(e.target.value)}>
            <option value="normal">Normal</option>
            <option value="start">Start</option>
            <option value="top">Top</option>
          </select>
        </div>
      </div>

      <div className="section-title">Select Wall Image</div>
      <div className="image-selection-container">
        <div className="image-selection">
          {images.map((image, index) => (
            <img
              key={image}
              src={`${process.env.PUBLIC_URL}/${image}?v=2`}
              alt={`Wall ${index + 1}`}
              className={`image-preview ${selectedImage === image ? 'selected' : ''}`}
              onClick={() => setSelectedImage(image)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedImage(image);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={selectedImage === image}
            />
          ))}
        </div>
      </div>

      {selectedImage && (
        <div className="hold-container">
          <div className="section-title">Preview & Place Holds</div>
          <div
            className="image-wrapper"
            ref={wrapperRef}
            onClick={handleImageClick}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ position: 'relative', width: '100%', aspectRatio: imageAspectRatio, maxWidth: 400, margin: '0 auto' }}
          >
            <img
              ref={imageRef}
              src={`${process.env.PUBLIC_URL}/${selectedImage}?v=2`}
              alt="Selected Wall"
              className="selected-image"
              onLoad={(event) => setImageAspectRatio(`${event.currentTarget.naturalWidth} / ${event.currentTarget.naturalHeight}`)}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', position: 'absolute', top: 0, left: 0 }}
            />
            {holds.map((hold, index) => {
              const displayArea = getImageDisplayArea();
              if (!displayArea) return null;
              const { left, top, width, height } = displayArea;
              const x = left + (hold.x / 100) * width;
              const y = top + (hold.y / 100) * height;
              return (
                <div
                  key={index}
                  className={`hold ${hold.shape} ${hold.fill} ${hold.size} ${hold.type}`}
                  style={{
                    position: 'absolute',
                    top: `${y}px`,
                    left: `${x}px`,
                    backgroundColor: hold.fill === 'filled' ? hold.color : 'transparent',
                    borderColor: hold.color,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  {hold.type === 'start' && <span className="hold-label">S</span>}
                  {hold.type === 'top' && <span className="hold-label">T</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="button-group">
        <button className="undo-button" onClick={handleUndo}>Undo</button>
        <button className="save-button" onClick={handleSaveRoute} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Route'}
        </button>
      </div>
    </div>
  );
};

export default CreateRoute;
